import {
  Editor,
  Element as SlateElement,
  type Element as SlateElementType,
  Transforms,
} from 'slate';

import type {
  AlignType,
  CustomEditor,
  CustomElement,
  CustomText,
} from '../types/SlateNodes';

const LIST_TYPES = ['numbered-list', 'bulleted-list'] as const;
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'] as const;

export type ListType = (typeof LIST_TYPES)[number];
export type MarkKey = Exclude<keyof CustomText, 'text'>;

/**
 * Toolbar identifiers — element types we toggle directly + the
 * alignment options that ride on a block's `align` field.
 */
export type BlockFormat = CustomElement['type'] | AlignType;

export function isAlignType(format: BlockFormat): format is AlignType {
  return TEXT_ALIGN_TYPES.includes(format as AlignType);
}

export function isListType(format: BlockFormat): format is ListType {
  return LIST_TYPES.includes(format as ListType);
}

export function isAlignableElement(
  element: CustomElement,
): element is Extract<CustomElement, { align?: AlignType }> {
  return 'align' in element;
}

export function isMarkActive(editor: CustomEditor, format: MarkKey): boolean {
  const marks = Editor.marks(editor) as Partial<CustomText> | null;
  return marks ? marks[format] === true : false;
}

export function toggleMark(editor: CustomEditor, format: MarkKey) {
  if (isMarkActive(editor, format)) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
}

export function isBlockActive(
  editor: CustomEditor,
  format: BlockFormat,
  matchOn: 'type' | 'align' = 'type',
): boolean {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n => {
        if (Editor.isEditor(n) || !SlateElement.isElement(n)) return false;
        const elem = n as unknown as CustomElement;
        if (matchOn === 'align' && isAlignableElement(elem)) {
          return elem.align === format;
        }
        return elem.type === format;
      },
    }),
  );
  return !!match;
}

/**
 * Flip a block-level format on the current selection. Mirrors
 * PopshopRN's `toggleBlock` — for alignment, sets the `align` field
 * on the wrapping block (or clears it). For lists, wraps the
 * selection in a list element; for everything else, swaps the
 * block's `type`.
 *
 * Slate's `Transforms.*` calls type their patches against the
 * Slate-augmented Element (from a different workspace slice), so
 * the `Partial<CustomElement>` patches are cast through `unknown` to
 * keep the type-checker satisfied without losing our local shape.
 */
export function toggleBlock(editor: CustomEditor, format: BlockFormat) {
  const isActive = isBlockActive(
    editor,
    format,
    isAlignType(format) ? 'align' : 'type',
  );
  const list = isListType(format);

  Transforms.unwrapNodes(editor, {
    match: n => {
      if (Editor.isEditor(n) || !SlateElement.isElement(n)) return false;
      const elem = n as unknown as CustomElement;
      return isListType(elem.type) && !isAlignType(format);
    },
    split: true,
  });

  if (isAlignType(format)) {
    Transforms.setNodes(editor, {
      align: isActive ? undefined : format,
    } as unknown as Partial<SlateElementType>);
    return;
  }

  Transforms.setNodes(editor, {
    type: isActive ? 'paragraph' : list ? 'list-item' : format,
  } as unknown as Partial<SlateElementType>);

  if (!isActive && list) {
    Transforms.wrapNodes(editor, {
      type: format,
      children: [],
    } as unknown as SlateElementType);
  }
}
